/**
 * XRRankingPage — v10.8.3
 * Ranking de estudiantes por escenario XR (MVP anatómico). Muestra
 * completion, accuracy, tiempo y ranking dentro del curso/escenario.
 */
import { useEffect, useState } from 'react';
import api from '../../api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Trophy, Medal, Clock, CheckCircle } from '@phosphor-icons/react';

export default function XRRankingPage() {
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/xr/scenarios')
      .then((r) => setScenarios(r.data?.scenarios || []))
      .catch(() => toast.error('No se pudieron cargar escenarios XR'));
  }, []);

  const loadRanking = async (scenarioId) => {
    setLoading(true);
    setSelectedId(scenarioId);
    try {
      const { data } = await api.get(`/xr/scenarios/${scenarioId}/ranking`);
      setRanking(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar ranking');
      setRanking(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="xr-ranking-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Trophy size={26} className="text-amber-500" weight="fill" />
          Ranking XR
        </h1>
        <p className="text-sm text-slate-500 mt-1">Ranking de estudiantes por escenario XR — completitud, precisión y tiempo.</p>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-slate-700">Escenario:</span>
            <Select onValueChange={loadRanking}>
              <SelectTrigger className="w-96"><SelectValue placeholder="Selecciona un escenario XR…" /></SelectTrigger>
              <SelectContent>
                {scenarios.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                ))}
                {scenarios.length === 0 && <SelectItem value="__none__" disabled>Sin escenarios XR aún</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="text-[10px] text-slate-400 mt-2">Tip: si no aparecen escenarios, seed con `POST /api/xr/mvp/anatomical/seed` o `POST /api/xr/mvp/codigo-azul/seed`.</div>
        </CardContent>
      </Card>

      {loading && <div className="text-slate-500 text-center py-6">Cargando ranking…</div>}

      {ranking && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{ranking.scenario_name} · {ranking.count} participantes</CardTitle>
          </CardHeader>
          <CardContent>
            {ranking.count === 0 ? (
              <div className="text-slate-400 text-center py-6 text-sm">Aún no hay sesiones registradas para este escenario.</div>
            ) : (
              <div className="space-y-2">
                {ranking.ranking.map((r) => (
                  <div key={r.session_id} className={`flex items-center gap-3 p-3 rounded-md border ${r.rank === 1 ? 'border-amber-300 bg-amber-50' : r.rank === 2 ? 'border-slate-300 bg-slate-50' : r.rank === 3 ? 'border-orange-300 bg-orange-50' : 'border-slate-100 bg-white'}`} data-testid={`ranking-row-${r.rank}`}>
                    <div className="w-8 text-center">
                      {r.rank <= 3 ? (
                        <Medal size={20} className={r.rank === 1 ? 'text-amber-500' : r.rank === 2 ? 'text-slate-400' : 'text-orange-500'} weight="fill" />
                      ) : (
                        <span className="text-slate-400 font-mono text-sm">#{r.rank}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{r.user_name}</div>
                      <div className="text-xs text-slate-500 truncate">{r.user_email}</div>
                    </div>
                    <div className="text-center min-w-[70px]">
                      <div className="text-[9px] uppercase text-slate-500">Completitud</div>
                      <div className="font-bold text-cyan-700">{r.completion_pct}%</div>
                    </div>
                    <div className="text-center min-w-[70px]">
                      <div className="text-[9px] uppercase text-slate-500">Precisión</div>
                      <div className="font-bold text-emerald-700">{r.accuracy_pct}%</div>
                    </div>
                    <div className="text-center min-w-[70px]">
                      <Badge variant="outline" className="gap-1"><CheckCircle size={10} weight="fill" className="text-emerald-500" />{r.correct}</Badge>
                    </div>
                    <div className="text-center min-w-[70px]">
                      <div className="text-[9px] uppercase text-slate-500 flex items-center justify-center gap-0.5"><Clock size={9} /> Tiempo</div>
                      <div className="font-mono text-slate-700 text-sm">{Math.round((r.elapsed_ms || 0) / 1000)}s</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
